import { Alert } from '@mantine/core';
import { useDebouncedValue, useInViewport, useResizeObserver } from '@mantine/hooks';
import { IconInfoCircle } from '@tabler/icons-react';
import cx from 'clsx';
import { Activity } from 'react';

import { UnsupportedChartRenderer } from '#core/components/dashboard/panels/chart/renderers/UnsupportedChartRenderer.tsx';
import { ErrorBoundaryWrapper } from '#core/components/error/ErrorBoundaryWrapper.tsx';
import { DownloadButton } from '#core/components/shared/DownloadButton.tsx';
import { ChartSizeContext } from '#core/contexts/ChartSizeContext.ts';
import { recordsToCsv } from '#core/lib/transforms/records-to-csv.ts';

import { createGridComponent } from '../shared/helpers.ts';
import classes from './Chart.module.css';
import { ChartSkeleton } from './ChartSkeleton.tsx';
import { CHART_RENDERERS } from './renderers/index.ts';
import { ChartSchema } from './schema.ts';

// showcase 4 unique chart styles: composite, radial bar, scatter and bubble

/**
 * Pure DashboardChart component for data visualization.
 *
 * This is a pure renderer - receives all data as props, no internal data fetching.
 * Works in both dashboard grid and chat block contexts.
 *
 * Supports all Mantine chart types:
 * - Cartesian: bar, line, area, composite
 * - Radial: pie, donut, radialBar
 * - Categorical: funnel, radar
 * - Correlation: scatter, bubble
 * - Compact: sparkline
 * - Grid: heatmap
 *
 * @example
 * ```tsx
 * <Chart
 *   chartType="bar"
 *   data={[{ name: 'A', value: 10 }, { name: 'B', value: 20 }]}
 *   xAxis="name"
 *   yAxis="value"
 *   title="My Chart"
 * />
 * ```
 */
export const Chart = createGridComponent(
  ({ data = [], xAxis = 'date', yAxis, title, chartType, series, curveType, referenceLines }) => {
    const { ref: viewportRef, inViewport } = useInViewport();
    const [sizeRef, rect] = useResizeObserver();
    const [debouncedRect] = useDebouncedValue(rect, 100);

    const isResizing = rect.width !== debouncedRect.width || rect.height !== debouncedRect.height;
    const contextValue = {
      width: debouncedRect.width,
      height: debouncedRect.height,
    };

    const config = ChartSchema.parse({
      type: 'chart',
      chartType,
      curveType,
      data,
      xAxis,
      yAxis,
      title,
      series,
      referenceLines,
    });

    const ChartRenderer = CHART_RENDERERS[chartType] ?? UnsupportedChartRenderer;
    const shouldDisplayChart = inViewport && contextValue.width > 0 && contextValue.height > 0;
    const csvRecords = recordsToCsv(data);

    if (!data || data.length === 0) {
      return (
        <Alert color="blue" icon={<IconInfoCircle size={16} />} variant="light">
          No data available for chart
        </Alert>
      );
    }

    return (
      <ErrorBoundaryWrapper
        context="chart"
        errorContext={`Type: ${chartType} | Rows: ${data.length}`}
        metadata={{ chartType, dataRowCount: data.length }}
        title="Chart failed to render"
        variant="alert"
      >
        <div
          className={cx(classes.outerContainer, isResizing && classes.isResizing)}
          data-chart-container
          ref={sizeRef}
        >
          <div className={classes.downloadOverlay}>
            <DownloadButton
              data={csvRecords}
              fileName={`${title ?? 'chart-data'}.csv`}
              mimeType="text/csv"
              size="sm"
              tooltip="Download CSV"
              variant="subtle"
            />
          </div>
          <div className={classes.innerContainer} ref={viewportRef}>
            <div className={classes.contents}>
              <ChartSizeContext value={contextValue}>
                {shouldDisplayChart && (
                  <Activity mode={isResizing ? 'hidden' : 'visible'}>
                    <ChartRenderer
                      config={config}
                      height={contextValue.height}
                      // onDataPointClick={handleDataPointClick}
                      width={contextValue.width}
                    />
                  </Activity>
                )}
              </ChartSizeContext>
            </div>
          </div>
        </div>
      </ErrorBoundaryWrapper>
    );
  },
  ChartSchema,
  ChartSkeleton,
);
